import React, { useState, useEffect, useRef, useTransition, useMemo, useCallback } from "react";
import { Button, Badge, DataTable, ActionButton, OrderStatus, getOrderStatusDisplayLabel } from "./ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem, MDBInput, MDBIcon, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody, MDBModalFooter, MDBTooltip } from "mdb-react-ui-kit";
import { api, setTenantApiScope } from "../services";
import PriceDisplay from "./PriceDisplay";
import { getImageUrl } from "../utils/utils";
import PrinterSettings from "./PrinterSettings";
import CustomDropdown from "./dropdown/CustomDropdown";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Toolbar from "./Toolbar";
import Actionbar from "./Actionbar";
import PendingOrderTimer from "./PendingOrderTimer";
import { useTranslation } from "react-i18next";
import { KpiCard } from "./ui/KpiCard";
import DeleteModal from "./Modals/DeleteModal";
import { useOrderNotification } from "../contexts/OrderNotificationContext";
import { useOrdersPageSession } from "../hooks/useOrdersPageSession";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isDashboardFulfillmentLocked, fulfillmentLockTooltipKey } from "../utils/posReadOnly";
import DisabledControlTooltipWrap from "./DisabledControlTooltipWrap";
import OrderDetailsDrawer from "./OrderDetailsDrawer";
import PlatformLogo, {
  ORDER_TABLE_PLATFORM_COL_WIDTH,
} from "./ui/PlatformLogo";
import { escapeHtmlText } from "../utils/orderLineLabels";
import { usePermission } from "../hooks/usePermission";
import {
  RBAC_MODULES,
  PERMISSION_ACTIONS,
} from "../config/routePermissions";
import AddressReveal from "./order/AddressReveal";
import OrderTypeBadge from "./order/OrderTypeBadge";
import PhoneOutlinedIcon from "./order/PhoneOutlinedIcon";
import CustomerInfoUpdatedBadge from "./order/CustomerInfoUpdatedBadge";
import {
  getOrderContactPhone,
  getOrderAddressDisplay,
  isAgentFamilyOrder,
  isCustomerInfoMasked,
  getPrivacyPlaceholderLabel,
} from "../utils/orderCustomerDisplay";
import "./order/orderCustomerDisplay.css";

/** TrackHub owns delivery completion only after a shipment is created. */
const hasActiveTrackHubShipment = (order) => {
  const shipmentId = order?.trackhub?.shipmentId;
  if (!shipmentId || !String(shipmentId).trim()) return false;
  const thStatus = String(order?.trackhub?.status || "").toUpperCase();
  return thStatus !== "DELIVERED";
};

/**
 * Manual Delivered is active only when Ready AND:
 * - Pickup, or
 * - Delivery with no TrackHub shipmentId
 * (TrackHub delivery with a shipment created on Accept is completed via webhook.)
 */
const canManuallyMarkDelivered = (order) => {
  if (String(order?.userGetMode || "").toLowerCase() === "pickup") return true;
  return !hasActiveTrackHubShipment(order);
};
// Platforms that support order timers
const PLATFORMS_WITH_TIMER = [
  'keeta',
  'jahez',
  'the chefz',
  'chefz',
  'ninja',
  'chat_agent',
  'chat agent',
  'digital_catalog',
  'digital catalog',
];

// Helper function to check if platform supports timer
const platformSupportsTimer = (platform) => {
  if (!platform) return false;
  return PLATFORMS_WITH_TIMER.includes(platform.toLowerCase());
};

/** Sum of line `final_price` (matches OrderDetailsDrawer / Keeta subtotal). */
const sumOrderLineFinalPrices = (products) =>
  (products || []).reduce((sum, p) => sum + (Number(p.final_price) || 0), 0);

// Format date to DD/MM/YYYY hh:mm:ss A
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);

  // Get day, month, year
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  // Get hours, minutes, seconds
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Determine AM/PM
  const ampm = hours >= 12 ? "PM" : "AM";

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const formattedHours = String(hours).padStart(2, "0");

  return `${day}/${month}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
};

/** Maps DataTable column ids to GET /orders sortBy fields */
const ORDER_TABLE_SORT_FIELDS = {
  platform: "platform",
  orderId: "order_id",
  customer: "customer_name",
  date: "createdAt",
  createdAt: "createdAt",
  total: "final_price",
  status: "status",
};

const formatOrderDateTime = (dateString, localeHint) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(localeHint || "en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return formatDateTime(dateString);
  }
};

/** Split date/time for All Orders Date column (Figma: stacked two lines). */
const formatOrderDateTimeParts = (dateString) => {
  if (!dateString) return { date: "—", time: "" };
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return { date: "—", time: "" };

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, "0");

  return {
    date: `${day}/${month}/${year}`,
    time: `${formattedHours}:${minutes}:${seconds} ${ampm}`,
  };
};

const READY_ELIGIBLE_STATUSES = new Set([
  "ACCEPTED",
  "ACCEPTED BY COURIER",
  "RIDER ASSIGNED",
]);

function canMarkReadyForDelivery(order) {
  return READY_ELIGIBLE_STATUSES.has(order?.status);
}

function Orders() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { can } = usePermission();
  const canUpdateOrder = can(RBAC_MODULES.ORDERS, PERMISSION_ACTIONS.EDIT);

  const getStatusDisplay = (order) => getOrderStatusDisplayLabel(order, t);

  const handlePrint = (order) => {
    // Find printer config for this order's branch
    const branchId = order.branch || order.branchId;
    const config = printerConfigs.find(
      (cfg) => cfg.branchId === branchId || cfg.branchId === (branchId && branchId.toString())
    );
    if (!config) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(
        "<h2>No printer configuration found for this branch.</h2>"
      );
      printWindow.document.close();
      return;
    }
    const numberOfCopies = config.numberOfCopies || 1;
    // Build a simple styled receipt based on config
    const paperStyle =
      config.paperSize === "A4"
        ? "width: 210mm;"
        : config.paperSize === "A5"
        ? "width: 148mm;"
        : "width: 105mm;";
    const logoHtml = config.logo
      ? `<img src="${config.logo}" alt="Logo" style="max-width:80px;max-height:80px;display:block;margin:0 auto 8px auto;" />`
      : "";
    const headerHtml = config.receipt_header
      ? `<div style="text-align:center;font-weight:bold;margin-bottom:8px;">${config.receipt_header}</div>`
      : "";
    const footerHtml = config.receipt_footer
      ? `<div style="text-align:center;font-size:12px;margin-top:12px;">${config.receipt_footer}</div>`
      : "";
    const currency = config.currency || "SAR";
    const fmt = (n) => (Number(n) || 0).toFixed(2);
    const isKeetaPrint = String(order.platform || "").toLowerCase() === "keeta";

    const orderRows = order.products
      .map(
        (product) => `
            <tr>
                <td>${product.quantity} x ${product.product_details?.name?.en || product.product_details?.name?.ar || product.product_id}</td>
                <td style="text-align:right;">${fmt(product.final_price)} ${currency}</td>
            </tr>
            ${
              product.modifiers && product.modifiers.length > 0
                ? product.modifiers
                    .map((mod) =>
                      mod.options
                        .map(
                          (opt) =>
                            `<tr><td style='padding-left:16px;'>+ ${opt.quantity} x ${opt.nameEn}</td><td style='text-align:right;'>${fmt(opt.final_price)} ${currency}</td></tr>`
                        )
                        .join("")
                    )
                    .join("")
                : ""
            }
        `
      )
      .join("");

    let tableTotalsHtml;
    if (isKeetaPrint) {
      const lineItemsSubtotal = (order.products || []).reduce(
        (sum, p) => sum + (Number(p.final_price) || 0),
        0
      );
      const feeRows = [];
      if (order.customer_fees?.delivery_fee != null) {
        feeRows.push(
          `<tr><td>Delivery Fee</td><td style="text-align:right;">${fmt(order.customer_fees.delivery_fee)} ${currency}</td></tr>`
        );
      }
      feeRows.push(
        `<tr><td>${escapeHtmlText(t("platformFees", "Platform fees"))}</td><td style="text-align:right;">${fmt(order.merchant_fees?.platform_service_fee)} ${currency}</td></tr>`
      );
      if (order.customer_fees?.tips != null) {
        feeRows.push(
          `<tr><td>Tips</td><td style="text-align:right;">${fmt(order.customer_fees.tips)} ${currency}</td></tr>`
        );
      }
      if (order.customer_fees?.top_up_amount != null) {
        feeRows.push(
          `<tr><td>Top-up Amount</td><td style="text-align:right;">${fmt(order.customer_fees.top_up_amount)} ${currency}</td></tr>`
        );
      }
      const feesHtml = feeRows.join("");
      const subtotalHtml = `<tr><td>Subtotal</td><td style="text-align:right;">${fmt(lineItemsSubtotal)} ${currency}</td></tr>`;
      const discountHtml = order.offer
        ? `<tr><td>Discount</td><td style="text-align:right;">- ${fmt(order.offer.amount)} ${currency}</td></tr>`
        : "";
      const totalHtml = `<tr style='font-weight:bold;'><td>Total</td><td style='text-align:right;'>${fmt(order.final_price)} ${currency}</td></tr>`;
      tableTotalsHtml = `${subtotalHtml}${feesHtml}${discountHtml}${totalHtml}`;
    } else {
      const discountHtml = order.offer
        ? `<tr><td>Discount</td><td style='text-align:right;'>- ${fmt(order.offer.amount)} ${currency}</td></tr>`
        : "";
      const subtotalHtml = config.receipt_show_subtotal
        ? `<tr><td>Subtotal</td><td style='text-align:right;'>${fmt(order.price)} ${currency}</td></tr>`
        : "";
      const totalHtml = `<tr style='font-weight:bold;'><td>Total</td><td style='text-align:right;'>${fmt(order.final_price)} ${currency}</td></tr>`;
      tableTotalsHtml = `${discountHtml}${subtotalHtml}${totalHtml}`;
    }
    const branchName = config.branchName || order.branchName || "";
    const storeName = config.storeName || "";
    const html = `
            <html><head><title>Print Order</title>
            <style>
                body { font-family: Arial, sans-serif; background: #fff; }
                .receipt { ${paperStyle} margin: 0 auto; padding: 16px; border: 1px solid #eee; }
                .receipt h2 { text-align: center; margin: 0 0 8px 0; }
                .receipt table { width: 100%; border-collapse: collapse; margin-top: 8px; }
                .receipt td { padding: 4px 0; font-size: 14px; }
                .receipt .meta { font-size: 13px; margin-bottom: 8px; }
                .receipt .footer { margin-top: 16px; font-size: 12px; text-align: center; color: #888; }
            </style>
            </head><body onload="window.print()">
            <div class="receipt">
                ${logoHtml}
                <h2>${storeName}</h2>
                <div class="meta">Branch: ${branchName}<br>Order #: ${order.order_id}<br>Date: ${new Date(order.createdAt).toLocaleString()}</div>
                ${headerHtml}
                <table>
                    ${orderRows}
                    ${tableTotalsHtml}
                </table>
                ${footerHtml}
                <div class="footer">Printer: ${config.printerType}, Paper: ${config.paperSize}</div>
            </div>
            </body></html>
        `;
    for (let i = 0; i < numberOfCopies; i++) {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        // Popup blocked, skip this copy silently
        continue;
      }
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  // Prefer platform external ID; for DC/Order Agent show readable order_id in
  // primary UI — External ID is rendered separately in the table.
  const getExternalOrderId = (order) => {
    if (!order) return '';
    const platform = String(order.platform || '').toUpperCase();
    const isAgentFamily =
      order.is_chat_agent_order ||
      platform === 'DIGITAL_CATALOG' ||
      platform === 'CHAT_AGENT';
    if (isAgentFamily) {
      return (
        order.order_id ||
        order.chat_agent_external_id ||
        order.jahez_id ||
        order.external_id ||
        order._id ||
        ''
      );
    }
    return (
      order.jahez_id ||
      order.chat_agent_external_id ||
      order.external_id ||
      order.order_id ||
      order._id ||
      ''
    );
  };

  // Use global order notification context
  const {
    orderRefreshTrigger,
    handleDismiss: dismissGlobalNotification,
    soundEnabled,
    toggleSound,
    triggerOrderPopup,
    setTriggerOrderPopup,
  } = useOrderNotification();

  // Get URL search params for order ID
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState("All");
  const [viewMode, setViewMode] = useState("list"); // State for list or grid view
  const [orders, setOrders] = useState([]); // State for fetched orders
  const [freeModeTeasers, setFreeModeTeasers] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]); // State for filtered orders
  const [apps, setApps] = useState([]); // State for integrated apps
  const [selectedApp, setSelectedApp] = useState("Select App");
  const [selectedAppId, setSelectedAppId] = useState(null); // Track selected app ID
  const [orderSummary, setOrderSummary] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
  });
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [tableLoading, setTableLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    totalOrders: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    try {
      const saved = localStorage.getItem('persistentBranchFilter');
      return saved ? JSON.parse(saved) : "";
    } catch (error) {
      console.error('Error loading branch filter from localStorage:', error);
      return "";
    }
  });
  const [selectedBranchId, setSelectedBranchId] = useState(() => {
    try {
      const saved = localStorage.getItem('persistentBranchFilter');
      return saved ? JSON.parse(saved)?._id || null : null;
    } catch (error) {
      console.error('Error loading branch filter from localStorage:', error);
      return null;
    }
  });
  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const [rawPlatforms, setRawPlatforms] = useState([]);
  const [storeId, setStoreId] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Show skeletons on first render
  const [selectedDate, setSelectedDate] = useState(""); // To store the selected date
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [orderStats, setOrderStats] = useState({});
  const [printerConfigs, setPrinterConfigs] = useState([]);
  const [printData, setPrintData] = useState(null); // For print preview/modal
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const branchDropdownRef = useRef(null);
  const branchInputRef = useRef(null);
  const [branchDropdownWidth, setBranchDropdownWidth] = useState(180);
  const [highlightedBranchIndex, setHighlightedBranchIndex] = useState(-1);
  const branchItemRefs = useRef([]);
  const [tableSortColumn, setTableSortColumn] = useState("createdAt");
  const [orderSortField, setOrderSortField] = useState("createdAt");
  const [orderSortDirection, setOrderSortDirection] = useState("desc");
  const [stores, setStores] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isProcessingReject, setIsProcessingReject] = useState(false);
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
    setIsProcessingReject(false);
  };
  const modalContentRef = useRef(null);
  useEffect(() => {
    if (stores.length > 0) {
      const allBranches = stores.flatMap((store) =>
        (store.branches || []).map((branch) => {
          const localizedName = isArabic
            ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
            : branch?.name?.en || branch?.name;
          return {
            ...branch,
            displayName: `${localizedName} - (${store.name})`,
          };
        })
      );
      console.log("All branches", allBranches);

      setBranches(allBranches);
    }
  }, [stores, isArabic]);
  // Tooltip state for product name in quick view
  const [tooltip, setTooltip] = useState({
    idx: null,
    visible: false,
    text: "",
    x: 0,
    y: 0,
  });

  const filteredBranches = branches?.filter((branch) => {
    const localizedName = isArabic
      ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
      : branch?.name?.en || branch?.name;
    return (localizedName || "").toLowerCase().includes((String(searchTerm || "")).toLowerCase());
  });

  const platformOptions = useMemo(() => {
    const options = [{ id: "All", name: t("platforms.all", "All") }];
    if (rawPlatforms.length > 0) {
      rawPlatforms.forEach((p) => {
        options.push({
          id: p.key,
          name: isArabic ? p.nameAr : p.nameEn,
        });
      });
    }
    return options;
  }, [rawPlatforms, isArabic, t]);

  // Helper function to get platform ID as string
  const getPlatformId = (platform) => {
    if (!platform || platform === "All") return "";
    return typeof platform === 'string' ? platform : platform.id || platform;
  };

  // Save branch selection to localStorage
  const saveBranchToStorage = (branch) => {
    try {
      if (branch) {
        localStorage.setItem('persistentBranchFilter', JSON.stringify(branch));
      } else {
        localStorage.removeItem('persistentBranchFilter');
      }
    } catch (error) {
      console.error('Error saving branch filter to localStorage:', error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      // Close dropdown if click is outside both input and dropdown menu
      if (
        branchDropdownRef.current &&
        !branchDropdownRef.current.contains(event.target) &&
        branchInputRef.current &&
        !branchInputRef.current.contains(event.target)
      ) {
        setShowBranchDropdown(false);
        setHighlightedBranchIndex(-1);
      }
    }
    function handleEscape(event) {
      if (event.key === "Escape") {
        setShowBranchDropdown(false);
        setHighlightedBranchIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (branchInputRef.current) {
      setBranchDropdownWidth(branchInputRef.current.offsetWidth);
    }
  }, [showBranchDropdown]);

  useEffect(() => {
    if (
      showBranchDropdown &&
      highlightedBranchIndex >= 0 &&
      branchItemRefs.current[highlightedBranchIndex]
    ) {
      branchItemRefs.current[highlightedBranchIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedBranchIndex, showBranchDropdown]);

  const handleSelect = (branch) => {
    const localizedName = isArabic
      ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
      : branch?.name?.en || branch?.name;
    setSelectedBranch(localizedName);
    handleBranchSelect(branch);
    setSearchTerm("");
    setIsOpen(false);
  };
  // Initial data fetch on component mount
  useEffect(() => {
    console.log("📊 [ORDERS] Initial data fetch");
    fetchApps();
    fetchAllOrders();
    fetchBranches();
    fetchStoreId();
    fetchPrinterConfigs();
    fetchPlatforms();
  }, []);

  // Watch for new orders from global notification context
  useEffect(() => {
    if (orderRefreshTrigger > 0) {
      console.log("🔄 [ORDERS] Order refresh triggered by global notification");
      fetchAllOrders();
    }
  }, [orderRefreshTrigger]);

  // OrderNotificationContext → open OrderDetailsDrawer on /orders
  useEffect(() => {
    if (triggerOrderPopup) {
      console.log(
        "🔍 [ORDERS] Opening OrderDetailsDrawer for:",
        triggerOrderPopup
      );
      handleOrderClick(triggerOrderPopup);
      setTriggerOrderPopup(null);
    }
  }, [triggerOrderPopup]);

  // Watch for orderId in URL query params (when opened in new tab)
  useEffect(() => {
    const orderIdFromUrl = searchParams.get("orderId");
    if (orderIdFromUrl) {
      console.log("🔍 [ORDERS] Order ID from URL:", orderIdFromUrl);
      // Wait a bit for orders to load, then open the drawer
      const timer = setTimeout(() => {
        handleOrderClick(orderIdFromUrl);
        // Remove the orderId from URL after opening
        searchParams.delete("orderId");
        setSearchParams(searchParams);
      }, 1000); // Give time for orders to load

      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  useEffect(() => {
    if (storeId) {
      fetchBranches();
    }
  }, [storeId]);

  useEffect(() => {
    setTenantApiScope(storeId || null, selectedBranchId || null);
  }, [storeId, selectedBranchId]);

  useEffect(() => {
    const status = activeTab === "All" ? "" : activeTab;
    const branch = selectedBranchId ? selectedBranchId : "";
    const platform = getPlatformId(selectedPlatform);
    setTableLoading(true);
    fetchAllOrders(1, status, branch, platform);
  }, [activeTab, selectedBranchId]); // Combined dependencies

  useEffect(() => {
    filterOrders(); // Reapply filters whenever filter states or orders update
  }, [activeTab, orders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const status = activeTab === "All" ? "" : activeTab;
      const branch = selectedBranchId ? selectedBranchId : "";
      const platform = getPlatformId(selectedPlatform);
      setTableLoading(true);
      fetchAllOrders(1, status, branch, platform);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const status = activeTab === "All" ? "" : activeTab;
      const branch = selectedBranchId ? selectedBranchId : "";
      const platform = getPlatformId(selectedPlatform);
      setTableLoading(true);
      fetchAllOrders(1, status, branch, platform);
    }, 500);
    return () => clearTimeout(timer);
  }, [dateFrom, dateTo]);

  const fetchStoreId = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Ensure response has the expected structure
      if (
        !response.data ||
        !Array.isArray(response.data.stores) ||
        response.data.stores.length === 0
      ) {
        throw new Error("No stores found in the response");
      }

      // Extract the first store's ID
      const myStoreId = response.data.stores[0]._id;
      console.log("stores", response.data.stores);

      setStoreId(myStoreId);
      setStores(response.data.stores);
    } catch (error) {
      console.error(
        "Error fetching storeId:",
        error.response?.data || error.message
      );
    }
  };

  const listenForJahezOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/webhooks/jahez/orders", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.orders) {
        setOrders((prevOrders) => [...prevOrders, ...response.data.orders]);
        setFilteredOrders((prevOrders) => [
          ...prevOrders,
          ...response.data.orders,
        ]);
        calculateSummary([...orders, ...response.data.orders]);
      }
    } catch (err) {
      console.error("Error fetching Jahez webhook orders:", err.message);
    }
  };
  const registerJahezWebhook = async () => {
    try {
      const userToken = localStorage.getItem("token"); // User authentication token

      //  Fetch the app integration details to get Jahez API Key
      const appResponse = await api.get("/apps", {
        headers: { Authorization: `Bearer ${userToken}` },
      });

      const app = appResponse.data.apps.find(
        (app) => app.application === "Jahez"
      );

      if (!app || !app.accessToken || !app.clientId) {
        console.error(
          " No valid Jahez app integration found. Please check Jahez API key."
        );
        return;
      }

      const headers = {
        Authorization: `Bearer ${userToken}`, // User Token for authentication
        "x-api-key": app.clientId, //  Ensure correct Jahez API key is being sent
        "Content-Type": "application/json",
      };

      //  Register webhook with correct headers
      const response = await api.post(
        "/webhooks/jahez_create_endpoint",
        { url: "https://api.demo.loops.sa/webhooks/jahez_create" },
        { headers }
      );

      if (response.data.success) {
      } else {
        console.error(" Failed to register Jahez webhook:", response.data);
      }
    } catch (error) {
      console.error(
        " Error registering Jahez webhook:",
        error.response?.data || error.message
      );
    }
  };

  // const registerJahezWebhook = async () => {
  //     try {
  //         const userToken = localStorage.getItem("token"); //  User's Auth Token
  //         if (!userToken) {
  //             console.error(" User Token is missing");
  //             return;
  //         }

  //         //  Fetch the app integration details to get the Jahez API Token
  //         const appResponse = await api.get("/stores/my", {
  //             headers: { Authorization: `Bearer ${userToken}` }, // Use user token to fetch app details
  //         });

  //         if (!appResponse.data || appResponse.data.apps.length === 0) {
  //             console.error(" No apps found for the user");
  //             return;
  //         }

  //         const app = appResponse.data.apps.find(a => a.application === "Jahez");
  //         if (!app || !app.accessToken) {
  //             console.error(" Jahez App Integration Not Found");
  //             return;
  //         }

  //         const jahezToken = app.accessToken;

  //         const response = await api.post(
  //             "/webhooks/jahez_create_endpoint",
  //             { url: "https://api.demo.loops.sa/webhooks/jahez_create" },
  //             {
  //                 headers: {
  //                     Authorization: `Bearer ${jahezToken}`, //  Use Jahez API Token
  //                     "Content-Type": "application/json",
  //                 },
  //             }
  //         );

  //         console.log(" Jahez Webhook registered successfully", response.data);
  //     } catch (error) {
  //         console.error(" Error registering Jahez webhook:", error.response?.data || error.message);
  //     }
  // };

  // Call this function on mount
  // useEffect(() => {
  //     registerJahezWebhook();
  // }, []);

  const filterOrders = () => {
    let updatedOrders = [...orders];

    // Filter by status
    if (activeTab !== "All") {
      updatedOrders = updatedOrders.filter(
        (order) => order.status === activeTab
      );
    }

    // Search handled by backend

    setFilteredOrders(updatedOrders); // Update the filtered orders state
  };

  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(response.data.apps || []);
    } catch (err) {
      console.error("Error fetching apps:", err.message);
    }
  };

  const handleViewDetails = (orderId) => {
    // Open OrderDetailsDrawer for this order
    navigate(`/orders/${orderId}`);
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(orderId === expandedOrderId ? null : orderId);
  };

  const fetchAllOrders = async (
    page = 1,
    status = "",
    branch = "",
    platform = "",
    sortBy = orderSortField,
    sortOrder = orderSortDirection,
    limit = itemsPerPage
  ) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          status,
          branch,
          delivery_service: platform && platform !== "All" ? (typeof platform === 'string' ? platform : platform.id || platform) : undefined,
          search: (searchQuery || "").trim() || undefined,
          dateFrom: (dateFrom || "").trim() || undefined,
          dateTo: (dateTo || "").trim() || undefined,
          sortBy,
          sortOrder,
        },
      });
      const {
        orders: fetchedOrders,
        totalOrders,
        totalPages,
        statusCounts,
      } = response.data;
      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders); // Initialize filtered orders
      setFreeModeTeasers(Array.isArray(response.data.freeMode) ? response.data.freeMode : []);
      calculateSummary(fetchedOrders); // Update order summary
      setTotalOrderCount(totalOrders);
      setOrderStats(statusCounts);
      setPagination({ totalOrders, totalPages, currentPage: page });
    } catch (err) {
      console.error("Error fetching orders:", err.message);
    } finally {
      setIsLoading(false); // Hide skeletons after data is loaded or errored
      setTableLoading(false);
    }
  };

  const reloadOrdersRef = useRef(() => {});
  reloadOrdersRef.current = () => {
    fetchAllOrders();
  };
  useOrdersPageSession(useCallback(() => reloadOrdersRef.current(), []));

  const handleOrderClick = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedOrderDetails(response.data.data);
      setDrawerOpen(true);
    } catch (err) {
      console.error(
        "Error fetching order details:",
        err.response?.data || err.message
      );
      alert(t("Failed to fetch order details. Please try again."));
    }
  };

  const calculateSummary = (orders) => {
    const summary = {
      total: orders.length,
      pending: 0,
      completed: 0,
      cancelled: 0,
    };
    orders.forEach((order) => {
      if (order.status === "Pending") summary.pending += 1;
      else if (order.status === "Completed") summary.completed += 1;
      else if (order.status === "Cancelled") summary.cancelled += 1;
    });
    setOrderSummary(summary);
  };

  const filterOrdersByStatus = (status) => {
    if (status === "All") {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter((order) => order.status === status);
      setFilteredOrders(filtered);
    }
    setActiveTab(status);
  };

  const handleAppSelect = (app) => {
    setSelectedApp(app.application);
    setSelectedAppId(app._id);
    fetchOrdersByApp(app._id, 1); // Fetch orders for the selected app (page 1)
  };

  const fetchBranches = async () => {
    if (!storeId) {
      console.error("Store ID is not set. Cannot fetch branches.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // setBranches(response.data.branches || []);
    } catch (err) {
      console.error(
        "Error fetching branches:",
        err.response?.data || err.message
      );
      alert(t("Failed to fetch branches. Please try again later."));
    }
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/platforms", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.success && response.data.data) {
        setRawPlatforms(response.data.data.platforms || []);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  // Handle branch selection
  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch);
    const branchId = branch ? branch._id : null;
    setSelectedBranchId(branchId);
    if (branch) {
      saveBranchToStorage(branch);
    } else {
      // Clear storage when "all" is selected
      try {
        localStorage.removeItem('persistentBranchFilter');
      } catch (error) {
        console.error('Error clearing branch filter from localStorage:', error);
      }
    }
    const status = activeTab === "All" ? "" : activeTab;
    const platform = getPlatformId(selectedPlatform);
    setTableLoading(true);
    fetchAllOrders(1, status, branchId, platform);
  };

  const fetchOrdersByBranch = async (branchId, page = 1) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(
        `/branches/${branchId}/orders?page=${page}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const { orders: fetchedOrders, totalOrders, totalPages } = response.data;
      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders); // Initialize filtered orders
      calculateSummary(fetchedOrders); // Update order summary
      setPagination({ totalOrders, totalPages, currentPage: page });
    } catch (err) {
      console.error(
        "Error fetching orders by branch:",
        err.response?.data || err.message
      );
    }
  };

  // const handleAction = async (orderId, action) => {
  //     try {
  //         const token = localStorage.getItem('token');

  //         // Send API request
  //         const response = await api.patch(
  //             `/orders/${orderId}/status`,
  //             { status: action === 'Accept' ? 'In Progress' : action },
  //             { headers: { Authorization: `Bearer ${token}` } }
  //         );

  //         // Update order status locally
  //         setFilteredOrders((prevOrders) =>
  //             prevOrders.map((order) =>
  //                 order.order_id === orderId
  //                     ? { ...order, status: response.data.order.status }
  //                     : order
  //             )
  //         );

  //         console.log('Order status updated:', response.data);
  //     } catch (error) {
  //         console.error('Error updating order status:', error.response?.data || error.message);
  //         alert('Failed to update order status. Please try again.');
  //     }
  // };

  const handleAction = async (appId, orderId, action) => {
    if (!canUpdateOrder) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    try {
      // Stop global notification audio when taking action on an order
      dismissGlobalNotification();

      const token = localStorage.getItem("token");
      const response = await api.patch(
        `/orders/${orderId}/status`,
        {
          status: action,
          appId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Send status update to Jahez Webhook - Moved to backend
      // if (response.data.success) {
      //     await api.post('/webhooks/status_update', {
      //         jahezOrderId: orderId,
      //         status: action === 'Accept' ? 'A' : action === 'Reject' ? 'R' : action,
      //         reason: action === 'Reject' ? 'Item not available' : ''
      //     });
      // }

      setFilteredOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.order_id === orderId
            ? { ...order, status: response.data.order.status }
            : order
        )
      );

      fetchAllOrders();
    } catch (error) {
      console.error(
        "Error updating order status:",
        error.response?.data || error.message
      );
      const apiMessage =
        (String(i18n.language || "").startsWith("ar") &&
          error.response?.data?.message_ar) ||
        error.response?.data?.message ||
        error.response?.data?.error_code ||
        null;
      if (apiMessage) {
        toast.error(
          typeof apiMessage === "string" ? apiMessage : t("Failed to update order status")
        );
      }
      fetchAllOrders();
      //alert('Failed to update order status. ');
    }
  };

  const fetchOrdersByApp = async (
    appId,
    page = 1,
    sortBy = orderSortField,
    sortOrder = orderSortDirection,
    limit = itemsPerPage
  ) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/apps/${appId}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          dateFrom: (dateFrom || "").trim() || undefined,
          dateTo: (dateTo || "").trim() || undefined,
          search: (searchQuery || "").trim() || undefined,
          sortBy,
          sortOrder,
        },
      });

      const { orders: fetchedOrders, totalOrders, totalPages } = response.data;
      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders); // Initialize filtered orders
      calculateSummary(fetchedOrders); // Update order summary
      setPagination({ totalOrders, totalPages, currentPage: page });
    } catch (err) {
      console.error(
        "Error fetching orders by app:",
        err.response?.data || err.message
      );
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const applyTableSort = (column, direction) => {
    const apiField = ORDER_TABLE_SORT_FIELDS[column];
    if (!apiField) return;

    setTableSortColumn(column);
    setOrderSortField(apiField);
    setOrderSortDirection(direction);
    setTableLoading(true);

    const status = activeTab === "All" ? "" : activeTab;
    const branch = selectedBranchId ? selectedBranchId : "";
    const platform = getPlatformId(selectedPlatform);

    if (selectedAppId) {
      fetchOrdersByApp(selectedAppId, 1, apiField, direction);
    } else {
      fetchAllOrders(1, status, branch, platform, apiField, direction);
    }
  };

  const handleOpenOrderDisplay = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    } else if (document.documentElement.webkitRequestFullscreen) {
      document.documentElement.webkitRequestFullscreen();
    } else if (document.documentElement.msRequestFullscreen) {
      document.documentElement.msRequestFullscreen();
    }
    navigate("/orders-display");
  };

  const handlePageChange = (page) => {
    if (selectedAppId) {
      fetchOrdersByApp(selectedAppId, page);
    } else {
      setTableLoading(true);
      const status = activeTab === "All" ? "" : activeTab;
      const branch = selectedBranchId ? selectedBranchId : "";
      const platform = getPlatformId(selectedPlatform);
      fetchAllOrders(page, status, branch, platform);
    }
  };

  const handlePageSizeChange = (size) => {
    setItemsPerPage(size);
    setTableLoading(true);
    const status = activeTab === "All" ? "" : activeTab;
    const branch = selectedBranchId ? selectedBranchId : "";
    const platform = getPlatformId(selectedPlatform);
    if (selectedAppId) {
      fetchOrdersByApp(
        selectedAppId,
        1,
        orderSortField,
        orderSortDirection,
        size
      );
    } else {
      fetchAllOrders(
        1,
        status,
        branch,
        platform,
        orderSortField,
        orderSortDirection,
        size
      );
    }
  };

  const orderStatuses = [
    { id: "All", name: `${t("ordersPage.filtertabs.all")}` },
    { id: "PENDING", name: `${t("ordersPage.filtertabs.new")}` },
    { id: "ACCEPTED", name: `${t("ordersPage.filtertabs.accepted")}` },
    {
      id: "READY FOR DELIVERY",
      name: `${t("ordersPage.filtertabs.readyForDelivery")}`,
    },
    // { id: "OUT FOR DELIVERY", name: "Out for delivery" },
    // { id: "DELIVERED", name: "Delivered" },
    { id: "CANCELLED", name: `${t("ordersPage.filtertabs.cancelled")}` },
    { id: "REJECTED", name: `${t("ordersPage.filtertabs.rejected")}` },
    { id: "TIME OUT", name: `${t("ordersPage.filtertabs.timeout")}` },
  ];

  const orderStatusTabs = (
    <div className="oh-orders-tabs" role="tablist" aria-label={t("ordersPage.title")}>
      {orderStatuses.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`oh-orders-tabs__item${isActive ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.name}</span>
          </button>
        );
      })}
    </div>
  );

  // useEffect for platform changes
  useEffect(() => {
    if (selectedPlatform !== "All") {
      const status = activeTab === "All" ? "" : activeTab;
      const branch = selectedBranchId ? selectedBranchId : "";
      const platformId = getPlatformId(selectedPlatform);
      setTableLoading(true);
      fetchAllOrders(1, status, branch, platformId);
    }
  }, [selectedPlatform]);

  // Sync with localStorage when branches are loaded
  useEffect(() => {
    if (branches.length > 0 && !selectedBranch) {
      try {
        const saved = localStorage.getItem('persistentBranchFilter');
        if (saved) {
          const savedBranch = JSON.parse(saved);
          // Check if saved branch still exists in current branches
          const existingBranch = branches.find(b => b._id === savedBranch._id);
          if (existingBranch) {
            setSelectedBranch(existingBranch);
            setSelectedBranchId(existingBranch._id);
          }
        }
      } catch (error) {
        console.error('Error syncing branch from localStorage:', error);
      }
    }
  }, [branches, selectedBranch]);

  const fetchPrinterConfigs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/printer-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrinterConfigs(response.data.data || []);
    } catch (err) {
      setPrinterConfigs([]);
    }
  };

  // Helper to get the list of selectable branches (excluding the selected one)
  const getSelectableBranches = () => {
    const list =
      (searchTerm && String(searchTerm).length > 0 ? filteredBranches : branches) || [];
    return list.filter((b) => b._id !== selectedBranchId);
  };

  // 1. Add state for the modal:
  const [quickViewOrder, setQuickViewOrder] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  // 3. Add handler to open the modal:
  const handleQuickView = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuickViewOrder(response.data.data);
      setQuickViewOpen(true);
    } catch (err) {
      setQuickViewOrder(null);
      setQuickViewOpen(false);
    }
  };
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        quickViewOpen &&
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target)
      ) {
        setQuickViewOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [quickViewOpen]);
  const [isVisible, setIsVisible] = useState(false);
  const [showClass, setShowClass] = useState(false); // for fade-in CSS

  useEffect(() => {
    if (quickViewOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        setShowClass(true); // apply fade-in class after render
      });
    } else {
      setShowClass(false); // triggers fade-out
      const timeout = setTimeout(() => {
        setIsVisible(false); // unmount after animation
        setQuickViewOrder(null);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [quickViewOpen]);

  const orderTableColumns = useMemo(
    () => {
      const platformColumnWidth = ORDER_TABLE_PLATFORM_COL_WIDTH;

      return [
      {
        id: "platform",
        header: t("ordersPage.orderTable.platform"),
        sortable: true,
        width: platformColumnWidth,
        minWidth: platformColumnWidth,
        className: "oh-table-td--platform",
        headerClassName: "oh-table-th--platform",
        render: (order) => {
          if (!order.platform) return null;
          return (
            <span className="orders-table-platform-logo-wrap">
              <PlatformLogo platform={order.platform} />
            </span>
          );
        },
      },
      {
        id: "orderId",
        header: t("ordersPage.orderTable.orderId"),
        width: 220,
        sortable: true,
        nowrap: true,
        className: "oh-table-td--order-id",
        headerClassName: "oh-table-th--order-id",
        render: (order) => (
          <div className="orders-table-id-cell">
            <button
              type="button"
              className="orders-table-order-id-link"
              title={order.order_id}
              onClick={() => handleOrderClick(order.order_id)}
            >
              {order.order_id}
            </button>
            {order.jahez_id || order.chat_agent_external_id ? (
              <button
                type="button"
                className="orders-table-order-id-link orders-table-order-id-link--muted"
                title={`${t("External ID")}: ${order.jahez_id || order.chat_agent_external_id}`}
                onClick={() => handleOrderClick(order.order_id)}
              >
                {t("External ID")}: {order.jahez_id || order.chat_agent_external_id}
              </button>
            ) : null}
          </div>
        ),
      },
      {
        id: "customer",
        header: t("ordersPage.orderTable.customer"),
        width: 168,
        sortable: true,
        strong: true,
        className: "oh-table-td--customer",
        headerClassName: "oh-table-th--customer",
        render: (order) => {
          const masked = isCustomerInfoMasked(order);
          const phone = masked
            ? getPrivacyPlaceholderLabel(t)
            : getOrderContactPhone(order);
          const name = masked
            ? getPrivacyPlaceholderLabel(t)
            : order.customer_name || "—";
          return (
            <div
              className="orders-table-customer-cell"
              title={masked ? undefined : order.customer_name || undefined}
            >
              <span className="orders-table-customer-cell__name">{name}</span>
              <CustomerInfoUpdatedBadge order={order} t={t} mode="customer" />
              <span className="orders-table-customer-cell__phone">
                {!masked ? (
                  <PhoneOutlinedIcon
                    size={12}
                    className="orders-table-customer-cell__phone-icon"
                  />
                ) : null}
                <span className="orders-table-customer-cell__phone-text">
                  {phone || t("N/A", "N/A")}
                </span>
              </span>
            </div>
          );
        },
      },
      {
        id: "address",
        header: t("ordersPage.orderTable.address", "Address"),
        width: 120,
        className: "oh-table-td--address",
        headerClassName: "oh-table-th--address",
        render: (order) => {
          const masked = isCustomerInfoMasked(order);
          return (
            <div
              className="orders-table-address-cell"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {masked ? (
                <span className="orders-table-privacy-placeholder">
                  {getPrivacyPlaceholderLabel(t)}
                </span>
              ) : (
                <AddressReveal address={getOrderAddressDisplay(order)} />
              )}
              <CustomerInfoUpdatedBadge order={order} t={t} mode="address" />
            </div>
          );
        },
      },
      {
        id: "fulfillmentType",
        header: t("ordersPage.orderTable.fulfillmentType", "Fulfilment Type"),
        width: 140,
        className: "oh-table-td--fulfillment",
        headerClassName: "oh-table-th--fulfillment",
        render: (order) => <OrderTypeBadge order={order} />,
      },
      {
        id: "products",
        header: t("product"),
        width: 88,
        nowrap: true,
        className: "oh-table-td--products",
        headerClassName: "oh-table-th--products",
        render: (order) => (
          <span className="orders-table-products-cell">
            {order.products.length} {t("items")}
          </span>
        ),
      },
      {
        id: "date",
        header: t("ordersPage.orderTable.date", "Date"),
        width: 110,
        sortable: true,
        className: "oh-table-td--date",
        headerClassName: "oh-table-th--date",
        render: (order) => {
          const { date, time } = formatOrderDateTimeParts(order.createdAt);
          return (
            <div
              className="orders-table-date-cell"
              title={time ? `${date} ${time}` : date}
            >
              <span className="orders-table-date-cell__date">{date}</span>
              {time ? (
                <span className="orders-table-date-cell__time">{time}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "total",
        header: t("ordersPage.orderTable.total"),
        width: 112,
        sortable: true,
        nowrap: true,
        className: "oh-table-td--total",
        headerClassName: "oh-table-th--total",
        render: (order) => (
          <>
            <PriceDisplay
              value={Number(order.final_price) || 0}
              className="orders-table-price"
              iconSize={14}
              color="#030f1b"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "18px",
                gap: "4px",
              }}
            />
            {expandedOrderId === order._id && order.offer && (
              <div className="orders-table-cell-muted pt-1">
                ({t("discount")}: -{order.offer.amount.toFixed(2)} SAR)
              </div>
            )}
          </>
        ),
      },
      {
        id: "status",
        header: t("ordersPage.orderTable.status"),
        width: 120,
        sortable: true,
        className: "oh-table-td--status",
        headerClassName: "oh-table-th--status",
        render: (order) =>
          order.status === "PROCESSED" ? (
            <MDBTooltip
              tag="div"
              wrapperProps={{ style: { display: "inline-block" } }}
              title="Orders processed from Portal or another service"
              placement="top"
            >
              <OrderStatus status={order.status}>
                {getStatusDisplay(order)}
              </OrderStatus>
            </MDBTooltip>
          ) : (
            <OrderStatus status={order.status}>
              {getStatusDisplay(order)}
            </OrderStatus>
          ),
      },
      {
        id: "timer",
        header: t("ordersPage.orderTable.timer", "Timer"),
        width: 84,
        className: "oh-table-td--timer",
        headerClassName: "oh-table-th--timer",
        render: (order) =>
          order.status === "PENDING" && platformSupportsTimer(order?.platform) ? (
            <PendingOrderTimer compact order={order} />
          ) : null,
      },
      {
        id: "actions",
        header: t("ordersPage.orderTable.actions"),
        width: 160,
        className: "oh-table-td--actions",
        headerClassName: "oh-table-th--actions",
        render: (order) => (
          <div
            className="oh-table-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {order.status === "PENDING" && (
              <>
                {!canUpdateOrder ? (
                  <ActionButton
                    variant="success"
                    disabled
                    title={t("noPermissionToAccessThis")}
                  >
                    {t("accept")}
                  </ActionButton>
                ) : (
                  <DisabledControlTooltipWrap
                    active={isDashboardFulfillmentLocked(order)}
                    title={t(fulfillmentLockTooltipKey(order))}
                  >
                    <ActionButton
                      variant="success"
                      disabled={isDashboardFulfillmentLocked(order)}
                      onClick={() => {
                        if (isDashboardFulfillmentLocked(order)) return;
                        handleAction(order.appId._id, order._id, "ACCEPTED");
                      }}
                    >
                      {t("accept")}
                    </ActionButton>
                  </DisabledControlTooltipWrap>
                )}
                {!canUpdateOrder ? (
                  <ActionButton
                    variant="reject"
                    disabled
                    title={t("noPermissionToAccessThis")}
                  >
                    {t("reject")}
                  </ActionButton>
                ) : (
                  <DisabledControlTooltipWrap
                    active={isDashboardFulfillmentLocked(order)}
                    title={t(fulfillmentLockTooltipKey(order))}
                  >
                    <ActionButton
                      variant="reject"
                      disabled={
                        isProcessingReject || isDashboardFulfillmentLocked(order)
                      }
                      onClick={() => {
                        if (
                          isDashboardFulfillmentLocked(order) ||
                          isProcessingReject
                        )
                          return;
                        setOrderToDelete(order);
                        setShowDeleteModal(true);
                      }}
                    >
                      {isProcessingReject &&
                      orderToDelete?._id === order._id
                        ? t("Rejecting...")
                        : t("reject")}
                    </ActionButton>
                  </DisabledControlTooltipWrap>
                )}
              </>
            )}
            {canMarkReadyForDelivery(order) &&
              (!canUpdateOrder ? (
                <ActionButton
                  variant="ready-for-delivery"
                  disabled
                  title={t("noPermissionToAccessThis")}
                >
                  {t("READY FOR DELIVERY")}
                </ActionButton>
              ) : (
                <DisabledControlTooltipWrap
                  active={isDashboardFulfillmentLocked(order)}
                  title={t(fulfillmentLockTooltipKey(order))}
                >
                  <ActionButton
                    variant="ready-for-delivery"
                    disabled={isDashboardFulfillmentLocked(order)}
                    onClick={() => {
                      if (isDashboardFulfillmentLocked(order)) return;
                      handleAction(
                        order.appId._id,
                        order._id,
                        "READY FOR DELIVERY"
                      );
                    }}
                  >
                    {t("READY FOR DELIVERY")}
                  </ActionButton>
                </DisabledControlTooltipWrap>
              ))}
            {order.status === "READY FOR DELIVERY" &&
              canManuallyMarkDelivered(order) &&
              (order.userGetMode === "pickup" || isAgentFamilyOrder(order)) &&
              (!canUpdateOrder ? (
                <ActionButton
                  variant="delivered"
                  disabled
                  title={t("noPermissionToAccessThis")}
                >
                  {order.userGetMode === "pickup"
                    ? t("delivered")
                    : t("ordersPage.privacy.markCompleted", "Mark as Completed")}
                </ActionButton>
              ) : (
                <DisabledControlTooltipWrap
                  active={isDashboardFulfillmentLocked(order)}
                  title={t(fulfillmentLockTooltipKey(order))}
                >
                  <ActionButton
                    variant="delivered"
                    disabled={isDashboardFulfillmentLocked(order)}
                    onClick={() => {
                      if (isDashboardFulfillmentLocked(order)) return;
                      handleAction(order.appId._id, order._id, "DELIVERED");
                    }}
                  >
                    {order.userGetMode === "pickup"
                      ? t("delivered")
                      : t("ordersPage.privacy.markCompleted", "Mark as Completed")}
                  </ActionButton>
                </DisabledControlTooltipWrap>
              ))}
          </div>
        ),
      },
    ];
    },
    [
      t,
      expandedOrderId,
      canUpdateOrder,
      isProcessingReject,
      orderToDelete,
      isArabic,
      handleOrderClick,
    ]
  );

  return (
    <div className="orders-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        {isLoading ? (
          <>
            {/* Header Skeleton */}
            <Skeleton height={40} width={220} style={{ marginBottom: 32 }} />
            {/* Stat Cards Skeleton */}
            <MDBRow className="mb-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <MDBCol xs="12" sm="6" md="3" className="mb-4" key={i}>
                  <Skeleton height={100} borderRadius={12} />
                </MDBCol>
              ))}
            </MDBRow>
            {/* Filters Skeleton */}
            <MDBRow className="mb-4">
              <MDBCol md="6">
                <Skeleton
                  height={36}
                  width={240}
                  style={{ marginBottom: 16 }}
                />
              </MDBCol>
              <MDBCol md="6" className="text-end">
                <Skeleton
                  height={36}
                  width={180}
                  style={{ marginBottom: 16 }}
                />
              </MDBCol>
            </MDBRow>
            {/* Tabs Skeleton */}
            <div className="mb-3">
              <Skeleton height={32} width={400} style={{ marginBottom: 16 }} />
            </div>
            {/* Table/Grid Skeleton */}
            {viewMode === "grid" ? (
              <MDBRow>
                {Array.from({ length: 6 }).map((_, i) => (
                  <MDBCol md="4" className="mb-4" key={i}>
                    <Skeleton height={220} borderRadius={12} />
                  </MDBCol>
                ))}
              </MDBRow>
            ) : (
              <MDBRow>
                <MDBCol md="12">
                  <Skeleton
                    height={48}
                    style={{ marginBottom: 8, borderRadius: 8 }}
                    count={8}
                  />
                </MDBCol>
              </MDBRow>
            )}
          </>
        ) : (
          <>
            <h1 className="page-title-header">{t("ordersPage.title")}</h1>
            {freeModeTeasers.map((row) => (
              <div className="alert alert-warning d-flex justify-content-between align-items-center" role="status" key={row.platform}>
                <span>{t("ordersPage.freeModeTeaser", { count: row.count, platform: row.platform })}</span>
                <Button type="button" onClick={() => navigate("/subscription")}>{t("ordersPage.upgrade")}</Button>
              </div>
            ))}

            <div className="oh-page-section oh-stats-actions-group">
            <div className="oh-kpi-row oh-kpi-row--4">
                <KpiCard
                  variant="orders"
                  title={t("ordersPage.totalOrderCard.title")}
                  value={
                    orderStats.PENDING +
                    orderStats.ACCEPTED +
                    orderStats.READY_FOR_DELIVERY +
                    orderStats.OUT_FOR_DELIVERY +
                    orderStats.DELIVERED +
                    orderStats.CANCELLED +
                    orderStats.REJECTED +
                    orderStats.TIME_OUT +
                    orderStats.COMPLETED
                  }
                  subtitle={t("ordersPage.totalOrderCard.subTitle")}
                />

                <KpiCard
                  variant="orders"
                  title={t("ordersPage.activeOrdersCard.title")}
                  value={
                    orderStats.PENDING +
                    orderStats.ACCEPTED +
                    orderStats.READY_FOR_DELIVERY +
                    orderStats.OUT_FOR_DELIVERY
                  }
                  breakdown={[
                    {
                      label: t("ordersPage.activeOrdersCard.ready"),
                      value: orderStats.READY_FOR_DELIVERY,
                    },
                    {
                      label: t("ordersPage.activeOrdersCard.pending"),
                      value: orderStats.PENDING,
                    },
                    {
                      label: t("ordersPage.activeOrdersCard.out"),
                      value: orderStats.OUT_FOR_DELIVERY,
                    },
                    {
                      label: t("ordersPage.activeOrdersCard.accepted"),
                      value: orderStats.ACCEPTED,
                    },
                  ]}
                />

                <KpiCard
                  variant="orders"
                  title={t("ordersPage.deliveredCard.title")}
                  value={orderStats.DELIVERED + orderStats.COMPLETED}
                  subtitle={t("ordersPage.deliveredCard.subTitle")}
                />

                <KpiCard
                  variant="orders"
                  title={t("ordersPage.problemOrderCard.title")}
                  value={
                    orderStats.CANCELLED +
                    orderStats.REJECTED +
                    orderStats.TIME_OUT
                  }
                  breakdown={[
                    {
                      label: t("ordersPage.problemOrderCard.cancelled"),
                      value: orderStats.CANCELLED,
                    },
                    {
                      label: t("ordersPage.problemOrderCard.rejected"),
                      value: orderStats.REJECTED,
                    },
                    {
                      label: t("ordersPage.problemOrderCard.timeout"),
                      value: orderStats.TIME_OUT,
                    },
                  ]}
                />
            </div>

            <Actionbar
              title={t("ordersPage.orderActionbar.title")}
              subtitle={t("ordersPage.orderActionbar.subTitle")}
              firstBtnText={soundEnabled ? t("disableSound") : t("enableSound")}
              firstBtnOnClick={toggleSound}
              firstBtnClass={soundEnabled ? "sound-on" : "sound-off"}
              firstBtnIcon={soundEnabled ? "speakerOn" : "speakerOff"}
              secondBtnText={t("View Order Display")}
              secondBtnOnClick={handleOpenOrderDisplay}
              secondBtnClass="ocean"
              secondBtnIcon="maximize"
              disabledBtn1={false}
              disabledBtn2={false}
              disabledBtn3={true}
            />
            </div>

            <div className="oh-page-section">
            <Toolbar
              toolbarVariant="orders"
              hideIcons={true}
              showBranchDropdown={true}
              branchWithAllOption={true}
              options={branches}
              handleSelect={handleBranchSelect}
              selectedOption={selectedBranch}
              placeholder={t("selectbranch")}
              backgroundColor="#ffffff"
              showPlatformDropdown={true}
              platformAfterDates={true}
              platformOptions={platformOptions}
              selectedPlatform={selectedPlatform}
              handlePlatformSelect={(id) => {
                const platformId = typeof id === 'string' ? id : id.id || id;
                setSelectedPlatform(platformId);
                const status = activeTab === "All" ? "" : activeTab;
                const branch = selectedBranchId ? selectedBranchId : "";
                setTableLoading(true);
                fetchAllOrders(1, status, branch, platformId);
              }}
              platformLabel={t("toolBar.platform", "Platform")}
              showSearch={true}
              searchPlaceHolder={t("Search Orders")}
              searchQuery={searchQuery}
              handleSearch={handleSearch}
              useCompactFilters={false}
              showDateRange={true}
              dateFrom={dateFrom}
              onDateFromChange={(e) => setDateFrom(e.target.value)}
              dateTo={dateTo}
              onDateToChange={(e) => setDateTo(e.target.value)}
              showFilter={true}
              showViewMode={true}
              viewMode={viewMode}
              handleViewChange={setViewMode}
              orderStatuses={orderStatuses}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            </div>

            <div className="oh-page-section">
            {/* Orders Content */}
            {viewMode === "grid" ? (
              <>
              <div className="oh-orders-tabs-wrap">{orderStatusTabs}</div>
              <div
                className="g-4"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: "1rem",
                  margin: 0,
                }}
              >
                {tableLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <MDBCol md="4" className="mb-4" key={`grid-skel-${i}`}>
                      <Skeleton height={220} borderRadius={12} />
                    </MDBCol>
                  ))
                ) : filteredOrders.length === 0 ? (
                  <div className="text-center text-muted py-4" style={{ gridColumn: '1 / -1' }}>
                    {t("noOrdersAvailable")}
                  </div>
                ) : filteredOrders.map((order) => (
                  <div key={order._id}>
                    <MDBCard 
                      className="order-card h-100 border-0 hover-shadow transition-all overflow-hidden"
                      style={{
                        borderRadius: "16px",
                        boxShadow: "0 2px 16px #F0F4F7",
                        overflow: "hidden",
                        transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        border: "1px solid rgba(0, 0, 0, 0.06)",
                        backgroundColor: "#fefefe",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "0 8px 32px #F0F4F7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 2px 16px #F0F4F7";
                      }}
                    >
                      <MDBCardBody className="d-flex flex-column p-3">
                        {/* Header */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                            <h6 className="mb-0 fw-bold text-truncate small">
                              <span className="text-muted me-1">#</span>
                              <a
                                href={`#order-${order.order_id}`}
                                className="text-decoration-none text-dark hover-text-primary"
                              >
                                {order.order_id}
                              </a>
                            </h6>
                            
                          </div>

                          {/* Customer Name + Platform Logo */}
                          <div className="d-flex justify-content-between align-items-center text-muted small flex-wrap">
                            <div className="fw-semibold text-dark text-truncate">
                              {order.customer_name}
                            </div>
                            {order.platform && (
                              <div className="d-flex align-items-center ms-2">
                                <PlatformLogo platform={order.platform} />
                              </div>
                            )}
                          </div>

                          <div className="text-muted small text-nowrap">
                            {formatDateTime(order.createdAt)}
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div style={{ marginBottom: "1rem" }}>
                          {order.status === "PROCESSED" ? (
                            <MDBTooltip
                              tag="div"
                              wrapperProps={{
                                style: { display: "inline-block" },
                              }}
                              title="Orders processed from Portal or another service"
                              placement="top"
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "rgba(248, 249, 250, 1)", // keep neutral
                                  color: "#6c757d", // default gray
                                  border: "1.5px solid #ced4da",
                                  padding: "0.15rem 0.6rem",
                                  borderRadius: "9999px",
                                  fontWeight: "600",
                                  fontSize: "0.65rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  userSelect: "none",
                                  minWidth: "80px",
                                  textAlign: "center",
                                  fontFamily:
                                    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
                                  whiteSpace: "nowrap",
                                  margin: "0 2px",
                                }}
                              >
                                {t(order.status, order.status)}
                              </span>
                            </MDBTooltip>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor:
                                  order.status === "PENDING"
                                    ? "rgba(255, 193, 7, 0.15)"
                                    : order.status === "ACCEPTED"
                                    ? "#d4edda"
                                    : order.status === "READY FOR DELIVERY"
                                    ? "rgba(13, 202, 240, 0.15)"
                                    : order.status === "OUT FOR DELIVERY"
                                    ? "#D9F0F0"
                                    : order.status === "TIME OUT"
                                    ? "rgba(108, 117, 125, 0.15)"
                                    : order.status === "CANCELLED"
                                    ? "#f8d7da"
                                    : order.status === "REJECTED"
                                    ? "#f8d7da"
                                    : order.status === "COMPLETED"
                                    ? "rgba(32, 201, 151, 0.15)"
                                    : order.status === "DELIVERED"
                                    ? "rgba(13, 110, 253, 0.15)"
                                    : order.status === "RIDER ASSIGNED"
                                    ? "rgba(111, 66, 193, 0.15)"
                                    : "rgba(248, 249, 250, 1)",
                                color:
                                  order.status === "PENDING"
                                    ? "#ffc107"
                                    : order.status === "ACCEPTED"
                                    ? "#155724"
                                    : order.status === "READY FOR DELIVERY"
                                    ? "#20c997"
                                    : order.status === "OUT FOR DELIVERY"
                                    ? "#004D40"
                                    : order.status === "TIME OUT"
                                    ? "#6c757d"
                                    : order.status === "CANCELLED"
                                    ? "#721c24"
                                    : order.status === "REJECTED"
                                    ? "#721c24"
                                    : order.status === "COMPLETED"
                                    ? "#20c997"
                                    : order.status === "DELIVERED"
                                    ? "#0d6efd"
                                    : order.status === "RIDER ASSIGNED"
                                    ? "#6f42c1"
                                    : "#6c757d",
                                border: `1.5px solid ${
                                  order.status === "PENDING"
                                    ? "#ffc107"
                                    : order.status === "ACCEPTED"
                                    ? "#155724"
                                    : order.status === "READY FOR DELIVERY"
                                    ? "#20c997"
                                    : order.status === "OUT FOR DELIVERY"
                                    ? "#004D40"
                                    : order.status === "TIME OUT"
                                    ? "#6c757d"
                                    : order.status === "CANCELLED"
                                    ? "#721c24"
                                    : order.status === "REJECTED"
                                    ? "#721c24"
                                    : order.status === "COMPLETED"
                                    ? "#20c997"
                                    : order.status === "DELIVERED"
                                    ? "#0d6efd"
                                    : order.status === "RIDER ASSIGNED"
                                    ? "#6f42c1"
                                    : "#ced4da"
                                }`,
                                padding: "0.15rem 0.6rem",
                                borderRadius: "9999px",
                                fontWeight: "600",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                userSelect: "none",
                                minWidth: "80px",
                                textAlign: "center",
                                fontFamily:
                                  "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
                                whiteSpace: "nowrap",
                                margin: "0 2px",
                              }}
                            >
                              {t(order.status, order.status)}
                            </span>
                          )}
                        </div>

                        {/* Order Summary */}
                        <div className="mb-3 p-3 bg-light rounded-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted small">
                              {order.products.length} {t("item")}
                              {order.products.length > 1 ? "" : ""}
                            </span>
                            <PriceDisplay
                              value={order.final_price.toFixed(2)}
                              iconSize="14"
                              color="#000"
                            />
                          </div>

                          {order.products.length > 0 && (
                            <div className="text-muted small">
                              <div>
                                <strong>{order.products[0].quantity} ×</strong>{" "}
                                {isArabic
                                  ? order.products[0].loops_product_id?.name
                                      ?.ar || order.products[0].product_id
                                  : order.products[0].loops_product_id?.name
                                      ?.en || order.products[0].product_id}
                              </div>
                              {order.products.length > 1 ? (
                                <div className="mt-1 text-muted">
                                  + {order.products.length - 1} {t("more item")}
                                  {order.products.length - 1 > 1 ? "s" : ""}
                                </div>
                              ) : (
                                <div className="mt-1 text-muted opacity-0">
                                  -
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Expanded Order Details */}
                        {expandedOrderId === order._id && (
                          <div className="mb-3 border-top pt-3">
                            <h6 className="fw-semibold mb-3 text-dark small">
                              {t("orderdetails")}
                            </h6>
                            <div className="mb-4">
                              {order.products.map((product, idx) => (
                                <div
                                  key={idx}
                                  className="mb-3 pb-3 border-bottom border-light"
                                >
                                  <div className="d-flex mb-2">
                                    <div className="flex-grow-1 me-2">
                                      <div className="fw-semibold text-dark small">
                                        {product.quantity} ×{" "}
                                        {isArabic
                                          ? product.loops_product_id?.name?.ar
                                          : product.loops_product_id?.name
                                              ?.en || product.product_id}
                                      </div>
                                    </div>
                                    <div className="flex-shrink-0 text-end">
                                      <PriceDisplay
                                        value={product.final_price.toFixed(2)}
                                        iconSize="9"
                                        color="#000"
                                        className="fw-bold"
                                      />
                                    </div>
                                  </div>

                                  {/* Modifiers */}
                                  {product.modifiers.length > 0 && (
                                    <div className="ms-3 text-muted small">
                                      {product.modifiers.map((mod, mIdx) =>
                                        mod.options.map((opt, oIdx) => (
                                          <div
                                            key={`${mIdx}-${oIdx}`}
                                            className="d-flex align-items-start py-1"
                                          >
                                            <span className="me-1">+</span>
                                            <div className="flex-grow-1">
                                              {opt.quantity} ×{" "}
                                              {isArabic
                                                ? opt.nameAr
                                                : opt.nameEn}
                                            </div>
                                            <PriceDisplay
                                              value={opt.final_price.toFixed(2)}
                                              iconSize="8"
                                              color="#6c757d"
                                            />
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}

                                  {/* Notes */}
                                  {product.notes && (
                                    <div className="mt-2 p-2 bg-warning bg-opacity-10 rounded-2 border-start border-warning border-2 small fst-italic text-muted">
                                      <i className="fas fa-sticky-note me-1"></i>{" "}
                                      {product.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Totals */}
                            <div className="bg-light p-3 rounded-3">
                              {order.offer && (
                                <div className="d-flex justify-content-between mb-2 small">
                                  <span className="text-success">
                                    <i className="fas fa-tag me-1"></i>
                                    {t("discount")}
                                  </span>
                                  <PriceDisplay
                                    value={order.offer.amount.toFixed(2)}
                                    iconSize="10"
                                    color="#198754"
                                  />
                                </div>
                              )}
                              <div className="d-flex justify-content-between mb-2 small">
                                <span>{t("subtotal")}</span>
                                <PriceDisplay
                                  value={
                                    order.platform?.toLowerCase() === "keeta"
                                      ? sumOrderLineFinalPrices(order.products)
                                      : order.price
                                  }
                                  iconSize="10"
                                  color="#6c757d"
                                />
                              </div>
                              <div className="d-flex justify-content-between border-top pt-2">
                                <span className="fw-bold small">
                                  {t("total")}
                                </span>
                                <PriceDisplay
                                  value={order.final_price.toFixed(2)}
                                  iconSize="12"
                                  color="#000"
                                  className="fw-bold"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Footer */}
                        <div className="mt-auto">
                          {/* Footer background styling */}
                          <div 
                            className="orders-card-actions"
                            style={{
                              backgroundColor: "",
                              borderTop: "1px solid rgba(0, 0, 0, 0.05)",
                              marginLeft: "-20px",
                              marginRight: "-20px",
                              marginBottom: "-16px",
                              padding: "12px",
                            }}
                          >
                            {order.status === "PENDING" && (
                              <>
                                {!(
                                  isProcessingReject &&
                                  orderToDelete?._id === order._id
                                ) &&
                                  (!canUpdateOrder ? (
                                    <MDBTooltip
                                      tag="span"
                                      title={t("noPermissionToAccessThis")}
                                    >
                                      <span>
                                        <button
                                          type="button"
                                          style={{
                                            borderRadius: "6px",
                                            padding: "8px 14px",
                                            border: "1px solid #27AE60",
                                            backgroundColor: "#ffffff",
                                            color: "#27AE60",
                                            fontWeight: 500,
                                            fontSize: "0.8rem",
                                            transition: "all 0.25s ease",
                                            cursor: "not-allowed",
                                            pointerEvents: "none",
                                            opacity: 0.85,
                                          }}
                                          disabled
                                          title="Accept"
                                        >
                                          {t("accept")}
                                        </button>
                                      </span>
                                    </MDBTooltip>
                                  ) : (
                                    <DisabledControlTooltipWrap
                                      active={isDashboardFulfillmentLocked(order)}
                                      title={t(fulfillmentLockTooltipKey(order))}
                                    >
                                      <button
                                        type="button"
                                        style={{
                                          borderRadius: "6px",
                                          padding: "8px 14px",
                                          border: "1px solid #27AE60",
                                          backgroundColor: "#ffffff",
                                          color: "#27AE60",
                                          fontWeight: 500,
                                          fontSize: "0.8rem",
                                          transition: "all 0.25s ease",
                                          cursor: isDashboardFulfillmentLocked(order)
                                            ? "not-allowed"
                                            : "pointer",
                                          opacity: isDashboardFulfillmentLocked(order)
                                            ? 0.6
                                            : 1,
                                        }}
                                        title={
                                          isDashboardFulfillmentLocked(order)
                                            ? undefined
                                            : "Accept"
                                        }
                                        disabled={isDashboardFulfillmentLocked(order)}
                                        onClick={() => {
                                          if (isDashboardFulfillmentLocked(order)) return;
                                          handleAction(
                                            order.appId._id,
                                            order._id,
                                            "ACCEPTED"
                                          );
                                        }}
                                      >
                                        {t("accept")}
                                      </button>
                                    </DisabledControlTooltipWrap>
                                  ))}

                                {!canUpdateOrder ? (
                                  <MDBTooltip
                                    tag="span"
                                    title={t("noPermissionToAccessThis")}
                                  >
                                    <span>
                                      <button
                                        type="button"
                                        style={{
                                          borderRadius: "6px",
                                          padding: "8px 14px",
                                          border: "1px solid #EB5757",
                                          backgroundColor: "#ffffff",
                                          color: "#EB5757",
                                          fontWeight: 500,
                                          fontSize: "0.8rem",
                                          transition: "all 0.25s ease",
                                          cursor: "not-allowed",
                                          pointerEvents: "none",
                                          opacity: 0.85,
                                        }}
                                        disabled
                                        title="Reject"
                                      >
                                        {t("reject")}
                                      </button>
                                    </span>
                                  </MDBTooltip>
                                ) : (
                                  <DisabledControlTooltipWrap
                                    active={isDashboardFulfillmentLocked(order)}
                                    title={t(fulfillmentLockTooltipKey(order))}
                                  >
                                    <button
                                      type="button"
                                      style={{
                                        borderRadius: "6px",
                                        padding: "8px 14px",
                                        border: "1px solid #EB5757",
                                        backgroundColor: "#ffffff",
                                        color: "#EB5757",
                                        fontWeight: 500,
                                        fontSize: "0.8rem",
                                        transition: "all 0.25s ease",
                                        cursor: isDashboardFulfillmentLocked(order)
                                          ? "not-allowed"
                                          : "pointer",
                                        opacity: isDashboardFulfillmentLocked(order)
                                          ? 0.6
                                          : 1,
                                      }}
                                      title={
                                        isDashboardFulfillmentLocked(order)
                                          ? undefined
                                          : "Reject"
                                      }
                                      disabled={isDashboardFulfillmentLocked(order)}
                                      onClick={() => {
                                        if (isDashboardFulfillmentLocked(order)) return;
                                        handleAction(
                                          order.appId._id,
                                          order._id,
                                          "REJECTED"
                                        );
                                      }}
                                    >
                                      {t("reject")}
                                    </button>
                                  </DisabledControlTooltipWrap>
                                )}
                              </>
                            )}

                            {canMarkReadyForDelivery(order) &&
                              (!canUpdateOrder ? (
                                <MDBTooltip
                                  tag="span"
                                  title={t("noPermissionToAccessThis")}
                                >
                                  <span>
                                    <button
                                      type="button"
                                      style={{
                                        borderRadius: "6px",
                                        padding: "8px 14px",
                                        border: "1px solid #2F80ED",
                                        backgroundColor: "#ffffff",
                                        color: "#2F80ED",
                                        fontWeight: 500,
                                        fontSize: "0.8rem",
                                        transition: "all 0.25s ease",
                                        cursor: "not-allowed",
                                        pointerEvents: "none",
                                        opacity: 0.85,
                                      }}
                                      disabled
                                    >
                                      {t("READY FOR DELIVERY")}
                                    </button>
                                  </span>
                                </MDBTooltip>
                              ) : (
                                <DisabledControlTooltipWrap
                                  active={isDashboardFulfillmentLocked(order)}
                                  title={t(fulfillmentLockTooltipKey(order))}
                                >
                                  <button
                                    type="button"
                                    style={{
                                      borderRadius: "6px",
                                      padding: "8px 14px",
                                      border: "1px solid #2F80ED",
                                      backgroundColor: "#ffffff",
                                      color: "#2F80ED",
                                      fontWeight: 500,
                                      fontSize: "0.8rem",
                                      transition: "all 0.25s ease",
                                      cursor: isDashboardFulfillmentLocked(order)
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: isDashboardFulfillmentLocked(order)
                                        ? 0.6
                                        : 1,
                                    }}
                                    disabled={isDashboardFulfillmentLocked(order)}
                                    onClick={() => {
                                      if (isDashboardFulfillmentLocked(order)) return;
                                      handleAction(
                                        order.appId._id,
                                        order._id,
                                        "READY FOR DELIVERY"
                                      );
                                    }}
                                  >
                                    {t("READY FOR DELIVERY")}
                                  </button>
                                </DisabledControlTooltipWrap>
                              ))}

                            {order.status === "READY FOR DELIVERY" &&
                              canManuallyMarkDelivered(order) &&
                              (order.userGetMode === "pickup" ||
                                isAgentFamilyOrder(order)) &&
                              (!canUpdateOrder ? (
                                <MDBTooltip
                                  tag="span"
                                  title={t("noPermissionToAccessThis")}
                                >
                                  <span>
                                    <button
                                      type="button"
                                      style={{
                                        borderRadius: "6px",
                                        padding: "8px 14px",
                                        border: "1px solid #2F80ED",
                                        backgroundColor: "#ffffff",
                                        color: "#2F80ED",
                                        fontWeight: 500,
                                        fontSize: "0.8rem",
                                        transition: "all 0.25s ease",
                                        cursor: "not-allowed",
                                        pointerEvents: "none",
                                        opacity: 0.85,
                                      }}
                                      disabled
                                    >
                                      {order.userGetMode === "pickup"
                                        ? t("Delivered")
                                        : t(
                                            "ordersPage.privacy.markCompleted",
                                            "Mark as Completed"
                                          )}
                                    </button>
                                  </span>
                                </MDBTooltip>
                              ) : (
                                <DisabledControlTooltipWrap
                                  active={isDashboardFulfillmentLocked(order)}
                                  title={t(fulfillmentLockTooltipKey(order))}
                                >
                                  <button
                                    type="button"
                                    style={{
                                      borderRadius: "6px",
                                      padding: "8px 14px",
                                      border: "1px solid #2F80ED",
                                      backgroundColor: "#ffffff",
                                      color: "#2F80ED",
                                      fontWeight: 500,
                                      fontSize: "0.8rem",
                                      transition: "all 0.25s ease",
                                      cursor: isDashboardFulfillmentLocked(order)
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: isDashboardFulfillmentLocked(order)
                                        ? 0.6
                                        : 1,
                                    }}
                                    disabled={isDashboardFulfillmentLocked(order)}
                                    onClick={() => {
                                      if (isDashboardFulfillmentLocked(order)) return;
                                      handleAction(
                                        order.appId._id,
                                        order._id,
                                        "DELIVERED"
                                      );
                                    }}
                                  >
                                    {order.userGetMode === "pickup"
                                      ? t("Delivered")
                                      : t(
                                          "ordersPage.privacy.markCompleted",
                                          "Mark as Completed"
                                        )}
                                  </button>
                                </DisabledControlTooltipWrap>
                              ))}

                            {order.status === "READY FOR DELIVERY" &&
                              order.userGetMode === "delivery" &&
                              !isAgentFamilyOrder(order) &&
                              (!canUpdateOrder ? (
                                <MDBTooltip
                                  tag="span"
                                  title={t("noPermissionToAccessThis")}
                                >
                                  <span>
                                    <button
                                      type="button"
                                      style={{
                                        borderRadius: "6px",
                                        padding: "8px 14px",
                                        border: "1px solid #2F80ED",
                                        backgroundColor: "#ffffff",
                                        color: "#2F80ED",
                                        fontWeight: 500,
                                        fontSize: "0.8rem",
                                        transition: "all 0.25s ease",
                                        cursor: "not-allowed",
                                        pointerEvents: "none",
                                        opacity: 0.85,
                                      }}
                                      disabled
                                    >
                                      {t("Out For Delivery")}
                                    </button>
                                  </span>
                                </MDBTooltip>
                              ) : (
                                <DisabledControlTooltipWrap
                                  active={isDashboardFulfillmentLocked(order)}
                                  title={t(fulfillmentLockTooltipKey(order))}
                                >
                                  <button
                                    type="button"
                                    style={{
                                      borderRadius: "6px",
                                      padding: "8px 14px",
                                      border: "1px solid #2F80ED",
                                      backgroundColor: "#ffffff",
                                      color: "#2F80ED",
                                      fontWeight: 500,
                                      fontSize: "0.8rem",
                                      transition: "all 0.25s ease",
                                      cursor: isDashboardFulfillmentLocked(order)
                                        ? "not-allowed"
                                        : "pointer",
                                      opacity: isDashboardFulfillmentLocked(order)
                                        ? 0.6
                                        : 1,
                                    }}
                                    disabled={isDashboardFulfillmentLocked(order)}
                                    onClick={() => {
                                      if (isDashboardFulfillmentLocked(order)) return;
                                      handleAction(
                                        order.appId._id,
                                        order._id,
                                        "OUT FOR DELIVERY"
                                      );
                                    }}
                                  >
                                    {t("Out For Delivery")}
                                  </button>
                                </DisabledControlTooltipWrap>
                              ))}

                            <button
                              onClick={() => handleQuickView(order.order_id)}
                              style={{
                                borderRadius: "6px",
                                padding: "8px 14px",
                                border: "1px solid #2F80ED",
                                backgroundColor: "#ffffff",
                                color: "#2F80ED",
                                fontWeight: 500,
                                fontSize: "0.8rem",
                                transition: "all 0.25s ease",
                                cursor: "pointer",
                              }}
                            >
                              {t("view")}
                            </button>
                          </div>

                          {/* Timer under buttons */}
                          {order.status === "PENDING" && (
                            <div className="mt-2">
                              {platformSupportsTimer(order.platform) && (
                                <PendingOrderTimer
                                  compact={true}
                                  order={order}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </MDBCardBody>
                    </MDBCard>
                  </div>
                ))}
              </div>
              </>
            ) : (
              <DataTable
                tableId="orders"
                toolbarStart={orderStatusTabs}
                className="orders-table-responsive"
                maxBodyHeight={700}
                columns={orderTableColumns}
                data={filteredOrders}
                rowKey="order_id"
                loading={tableLoading}
                emptyState={t("noOrdersAvailable")}
                sort={{
                  column: tableSortColumn,
                  direction: orderSortDirection,
                  onChange: ({ column, direction }) =>
                    applyTableSort(column, direction),
                }}
                pagination={{
                  page: pagination.currentPage,
                  pageSize: itemsPerPage,
                  total: pagination.totalOrders,
                  onPageChange: handlePageChange,
                  onPageSizeChange: handlePageSizeChange,
                  pageSizeOptions: [10, 20, 25, 50],
                  showLabel: t("Show", { defaultValue: "Show" }),
                  ofLabel: t("of", { defaultValue: "Of" }),
                  resultsLabel: t("results", { defaultValue: "results" }),
                  isRtl: isArabic,
                  previousLabel: t("previous"),
                  nextLabel: t("next"),
                }}
                isRtl={isArabic}
              />
            )}
            {viewMode === "grid" && (
            <div className="mt-3">
              <MDBRow className="pagination-margin">
                <MDBCol>
                  <div
                    className="d-flex justify-content-between align-items-center"
                    dir={isArabic ? "rtl" : "ltr"}
                  >
                    <Button
                      color="secondary"
                      outline
                      size="md"
                      disabled={pagination.currentPage === 1 || filteredOrders.length === 0}
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                    >
                      {t("previous")}
                    </Button>
                    {filteredOrders.length > 0 && pagination.totalPages > 0 && (
                      <span>
                        {t("page")} {pagination.currentPage} {t("of")} {" "}
                        {pagination.totalPages}
                      </span>
                    )}
                    <Button
                      color="secondary"
                      outline
                      size="md"
                      disabled={
                        pagination.currentPage === pagination.totalPages || filteredOrders.length === 0
                      }
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                    >
                      {t("next")}
                    </Button>
                  </div>
                </MDBCol>
              </MDBRow>
            </div>
            )}
            </div>

            {(quickViewOrder || isVisible) && (
              <div
                className={`modal fade ${
                  quickViewOpen ? "show fade-in" : "fade-out"
                }`}
                style={{
                  display: "block",
                  background: "rgba(0,0,0,0.2)",
                  backdropFilter: "blur(5px)",
                  WebkitBackdropFilter: "blur(5px)",
                  opacity: showClass ? 1 : 0,
                  pointerEvents: showClass ? "auto" : "none",
                  transition: "opacity 0.3s ease",
                }}
                tabIndex="-1"
                role="dialog"
              >
                <div
                  className="modal-dialog modal-lg modal-dialog-centered d-flex align-items-center justify-content-center"
                  role="document"
                >
                  <div ref={modalContentRef} className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title text-primary fw-semibold d-flex flex-column flex-md-row align-items-start align-items-md-center gap-1 gap-md-2">
                        <span className="d-flex align-items-center">
                          <i className="fas fa-receipt me-2"></i>
                          {t("orderdetails")}
                        </span>
                        <span className="text-muted small mobile-left-padding">
                    #{getExternalOrderId(quickViewOrder)}
                        </span>
                      </h5>

                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setQuickViewOpen(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div
                        className="order-details-container p-4 bg-white rounded-3"
                        style={{ maxHeight: "70vh", overflowY: "auto" }}
                      >
                        <div
                          className="row g-4 order-details-flex-row"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                          }}
                        >
                          {/* Products Section */}
                          <div
                            className="col-lg-7 col-md-6 order-details-products"
                            style={{ minWidth: 250 }}
                          >
                            <div className="products-section">
                              <h6 className="section-title mb-3 fw-semibold text-muted">
                                <i className="fas fa-shopping-cart me-2"></i>
                                {t("products")}
                              </h6>
                              <div className="products-list">
                                {quickViewOrder.products.map((product, idx) => {
                                  const fullName =
                                    (isArabic
                                      ? product.loops_product_id?.name?.ar ||
                                        product.product_details?.name?.ar
                                      : product.loops_product_id?.name?.en ||
                                        product.product_details?.name?.en) ||
                                    "";

                                  const maxLen = 28;
                                  const isTruncated = fullName.length > maxLen;
                                  const displayName = isTruncated
                                    ? fullName.slice(0, maxLen) + "..."
                                    : fullName;

                                  return (
                                    <div
                                      key={idx}
                                      className="product-item mb-4 pb-3 border-bottom"
                                      style={{ position: "relative" }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div
                                          className="d-flex align-items-center"
                                          style={{ minWidth: 0 }}
                                        >
                                          <span className="me-2 fw-semibold text-nowrap">
                                            {product.quantity}x
                                          </span>
                                          <div
                                            className="d-flex align-items-center"
                                            style={{ minWidth: 0 }}
                                          >
                                            <span
                                              className="d-flex align-items-center"
                                              style={{
                                                maxWidth: 200,
                                                fontWeight: 500,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}
                                            >
                                              <span
                                                className="text-truncate"
                                                title={
                                                  isTruncated ? fullName : ""
                                                }
                                              >
                                                {displayName}
                                              </span>
                                              {isTruncated && (
                                                <span
                                                  style={{
                                                    position: "relative",
                                                    display: "inline-block",
                                                    marginLeft: 6,
                                                  }}
                                                >
                                                  <i
                                                    className="fas fa-info-circle"
                                                    style={{
                                                      color: "#adb5bd",
                                                      cursor: "pointer",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      const rect =
                                                        e.currentTarget.getBoundingClientRect();
                                                      setTooltip({
                                                        idx,
                                                        visible: true,
                                                        text: fullName,
                                                        x:
                                                          rect.left +
                                                          rect.width / 2,
                                                        y: rect.top,
                                                      });
                                                    }}
                                                    onMouseLeave={() =>
                                                      setTooltip({
                                                        idx: null,
                                                        visible: false,
                                                        text: "",
                                                        x: 0,
                                                        y: 0,
                                                      })
                                                    }
                                                  />
                                                </span>
                                              )}
                                            </span>
                                            {tooltip.visible &&
                                              tooltip.idx === idx && (
                                                <div
                                                  style={{
                                                    position: "fixed",
                                                    left: tooltip.x,
                                                    top: tooltip.y - 40,
                                                    transform:
                                                      "translateX(-50%)",
                                                    background: "#333",
                                                    color: "#fff",
                                                    padding: "6px 12px",
                                                    borderRadius: "4px",
                                                    fontSize: "13px",
                                                    zIndex: 9999,
                                                    whiteSpace: "pre-line",
                                                    boxShadow:
                                                      "0 2px 8px rgba(0,0,0,0.15)",
                                                    maxWidth: "320px",
                                                    pointerEvents: "none",
                                                  }}
                                                >
                                                  {tooltip.text}
                                                </div>
                                              )}
                                          </div>
                                        </div>
                                        <div className="text-end">
                                          <span className="fw-semibold text-muted">
                                            {product.final_price.toFixed(2)} SAR
                                          </span>
                                        </div>
                                      </div>

                                      {/* Modifiers */}
                                      {product.modifiers.length > 0 && (
                                        <div className="ms-3 mt-2 small text-muted">
                                          {product.modifiers.map(
                                            (modifier, mIdx) => (
                                              <div key={mIdx}>
                                                {modifier.options.map(
                                                  (option, oIdx) => (
                                                    <div
                                                      key={oIdx}
                                                      className="d-flex justify-content-between align-items-center mb-1"
                                                    >
                                                      <span className="text-nowrap">
                                                        <i className="fas fa-plus me-1"></i>
                                                        {option.quantity}x{" "}
                                                        {isArabic
                                                          ? option.nameAr
                                                          : option.nameEn}
                                                      </span>
                                                      <span>
                                                        {option.final_price.toFixed(
                                                          2
                                                        )}{" "}
                                                        SAR
                                                      </span>
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      )}

                                      {/* Notes */}
                                      {product.notes && (
                                        <div className="mt-2 p-2 bg-warning bg-opacity-10 rounded-2 border-start border-warning border-2 small">
                                          <i className="fas fa-sticky-note me-1"></i>
                                          <em className="text-muted">
                                            {product.notes}
                                          </em>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Summary Section */}
                          <div
                            className="col-lg-5 col-md-6 order-details-summary"
                            style={{ minWidth: 200 }}
                          >
                            <div className="summary-section ps-md-3">
                              <h6 className="section-title mb-3 fw-semibold text-muted">
                                <i className="fas fa-calculator me-2"></i>
                                {t("ordersummary")}
                              </h6>
                              <div className="summary-content">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span
                                    className="text-nowrap"
                                    style={{ marginRight: 12 }}
                                  >
                                    {t("subtotal")}:
                                  </span>
                                  <PriceDisplay
                                    value={
                                      quickViewOrder.platform?.toLowerCase() === "keeta"
                                        ? sumOrderLineFinalPrices(quickViewOrder.products)
                                        : quickViewOrder.price
                                    }
                                    iconSize="14"
                                    color="#6c757d"
                                  />
                                </div>
                                {quickViewOrder.platform?.toLowerCase() === "keeta" && (
                                  <>
                                    {quickViewOrder.customer_fees?.delivery_fee != null && (
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-nowrap" style={{ marginRight: 12 }}>
                                          {t("deliveryfee")}:
                                        </span>
                                        <PriceDisplay
                                          value={quickViewOrder.customer_fees?.delivery_fee ?? 0}
                                          iconSize="14"
                                          color="#6c757d"
                                        />
                                      </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-nowrap" style={{ marginRight: 12 }}>
                                        {t("platformFees", "Platform fees")}:
                                      </span>
                                      <PriceDisplay
                                        value={quickViewOrder.merchant_fees?.platform_service_fee ?? 0}
                                        iconSize="14"
                                        color="#6c757d"
                                      />
                                    </div>
                                    {quickViewOrder.customer_fees?.tips != null && (
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-nowrap" style={{ marginRight: 12 }}>
                                          {t("tips")}:
                                        </span>
                                        <PriceDisplay
                                          value={quickViewOrder.customer_fees?.tips ?? 0}
                                          iconSize="14"
                                          color="#6c757d"
                                        />
                                      </div>
                                    )}
                                    {quickViewOrder.customer_fees?.top_up_amount != null && (
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-nowrap" style={{ marginRight: 12 }}>
                                          {t("Top-up Amount", "Top-up Amount")}:
                                        </span>
                                        <PriceDisplay
                                          value={quickViewOrder.customer_fees?.top_up_amount ?? 0}
                                          iconSize="14"
                                          color="#6c757d"
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                                {quickViewOrder.offer && (
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span
                                      className="text-nowrap text-success"
                                      style={{ marginRight: 12 }}
                                    >
                                      <i className="fas fa-tag me-1"></i>
                                      {t("discount")}:
                                    </span>
                                    <span className="fw-semibold text-success">
                                      -
                                      <PriceDisplay
                                        value={quickViewOrder.offer.amount.toFixed(
                                          2
                                        )}
                                        iconSize="14"
                                        color="#dc3545"
                                      />
                                    </span>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-3">
                                  <span
                                    className="fw-bold text-nowrap"
                                    style={{ marginRight: 12 }}
                                  >
                                    {t("total")}:
                                  </span>
                                  <div className="fw-bold">
                                    <PriceDisplay
                                      value={quickViewOrder.final_price.toFixed(
                                        2
                                      )}
                                      iconSize="16"
                                      color="#198754"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="mt-4 text-end">
                                <Button
                                  color="primary"
                                  size="md"
                                  onClick={() =>
                                    handleViewDetails(quickViewOrder.order_id)
                                  }
                                >
                                  <i className="fas fa-eye me-2"></i>
                                  {t("View Full Details")}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Responsive Styles */}
                      <style>{`
              @media (max-width: 768px) {
                .order-details-container {
                  padding: 12px !important;
                }
                .fade-in {
                  opacity: 1 !important;
                  pointer-events: auto !important;
                }
                @media (max-width: 768px) {
                  .mobile-left-padding {
                    padding-left: 15px !important;
                  }
                }
                .fade-out {
                  opacity: 0 !important;
                  pointer-events: none !important;
                }
                .order-details-flex-row {
                  flex-direction: column !important;
                  flex-wrap: nowrap !important;
                }
                .order-details-products, .order-details-summary {
                  min-width: 0 !important;
                  width: 100% !important;
                  max-width: 100% !important;
                }
              }
              @media (max-width: 1024px) {
                .order-details-flex-row {
                  flex-direction: column !important;
                  flex-wrap: nowrap !important;
                }
                .order-details-products, .order-details-summary {
                  min-width: 0 !important;
                  width: 100% !important;
                  max-width: 100% !important;
                }
                .order-details-summary {
                  padding-right: 16px !important;
                  word-break: break-word !important;
                }
              }
            `}</style>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </MDBContainer>

      {/* Delete/Reject/Cancel Modal - Outside the loop to prevent multiple instances */}
      <DeleteModal
        text={
          orderToDelete
            ? t("rejectOrderConfirm", {
                id: getExternalOrderId(orderToDelete),
              })
            : t("Are you sure you want to process this order?")
        }
        showModal={showDeleteModal && orderToDelete !== null && canUpdateOrder}
        isLoading={isProcessingReject}
        confirmText="reject"
        confirmIcon="fas fa-times"
        confirmDelete={async () => {
          if (orderToDelete && isDashboardFulfillmentLocked(orderToDelete)) {
            handleCloseModal();
            return;
          }
          if (orderToDelete && !isProcessingReject) {
            setIsProcessingReject(true);
            let actionSuccess = false;
            try {
              await handleAction(
                orderToDelete.appId._id,
                orderToDelete._id,
                "REJECTED"
              );
              
              actionSuccess = true;

              // Show success notification with order ID
                toast.success(
                  t("Order #{{id}} rejected successfully!", {
                    id: getExternalOrderId(orderToDelete),
                  })
                );
            } catch (error) {
              console.error("Error in confirmDelete:", error);
              toast.error(
                t("Failed to update order #{{id}}", {
                  id: getExternalOrderId(orderToDelete),
                })
              );
              actionSuccess = false;
            } finally {
              setIsProcessingReject(false);
            }
          }
          handleCloseModal();
        }}
        onClose={() => {
          if (!isProcessingReject) {
            handleCloseModal();
          }
        }}
      />

      {/* OrderDetailsDrawer (tabbed drawer) */}
      <OrderDetailsDrawer
        show={drawerOpen && !!selectedOrderDetails}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedOrderDetails(null);
        }}
        orderDetails={selectedOrderDetails}
        onOrderUpdate={(updated) => {
          if (updated && typeof updated === "object") {
            setSelectedOrderDetails((prev) =>
              prev ? { ...prev, ...updated } : updated
            );
          }
          fetchAllOrders();
        }}
        formatDateTime={formatDateTime}
        getExternalOrderId={getExternalOrderId}
      />

      <style>{`
        @media (max-width: 768px) {
          .branch-dropdown-responsive { width: 100% !important; margin-top: 10px !important; }
        }
      `}</style>
      <style>{`
.status-row span {
  white-space: nowrap;
  font-size: clamp(12px, 2vw, 15px);
}
@media (max-width: 600px) {
  .status-row {
    gap: 4px !important;
    font-size: 12px !important;
  }
}
.status-2x2-grid {
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  justify-items: center;
  align-items: center;
}
.status-2x2-grid .status-label-nowrap {
  white-space: nowrap;
  font-size: 14px;
  display: block;
  width: 100%;
  text-align: center;
}
@media (max-width: 900px) {
  .status-2x2-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px 8px !important;
  }
  .status-2x2-grid .status-label-nowrap {
    font-size: 14px !important;
  }
  .problem-timeout-span {
    grid-column: 1 / span 2;
  }
}
.problem-timeout-span {
  grid-column: 1 / span 2;
  text-align: center;
}
@media (max-width: 900px) {
  .problem-timeout-span {
    grid-column: 1 / span 1;
  }
}
`}</style>
    </div>
  );
}

export default Orders;
